import { Copy } from "@components:Copy";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { callOrDefault } from "@utils:general";
import { JsonEditor } from "json-edit-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdDataObject as JsonIcon,
  MdExpandMore as ExpandMoreIcon,
} from "react-icons/md";

interface JsonTableRendererProps {
  data: Record<string, never>;
}

export const JsonTableRenderer: React.FC<JsonTableRendererProps> = ({
  data,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          width: "100%",
          height: "100%",
          py: 0.5,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={handleOpenPopover}
      >
        <Chip
          label={t("components.object")}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            fontSize: "0.7rem",
            height: 18,
            flexShrink: 0,
            "& .MuiChip-label": {
              px: 0.5,
            },
          }}
        />

        <Typography
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "text.primary",
          }}
        >
          {t("components.keyCount", {
            value: callOrDefault(() => Object.keys(data).length, 0),
          })}
        </Typography>

        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{ flexShrink: 0 }}
        >
          <Copy
            text={callOrDefault(() => JSON.stringify(data, null, 2), "")}
            tooltipText={t("components.copyPreferences")}
          />
          <Tooltip title={t("components.viewJson")}>
            <IconButton size="small" sx={{ p: 0.25 }}>
              <ExpandMoreIcon size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Paper
          sx={{
            p: 2,
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <JsonIcon size={20} />
              <Typography variant="subtitle2" fontWeight={600}>
                {t("components.jsonData")}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            <JsonEditor data={data} restrictEdit restrictAdd restrictDelete />
          </Box>
        </Paper>
      </Popover>
    </>
  );
};
